<script setup lang="ts">
import { Field as AFormField, useForm } from "vee-validate";
import type { ButtonState, ContentItem } from "@edco/apollo";
import { toTypedSchema } from "@vee-validate/zod";
import { z } from "zod";
import type { Login } from "~/models/generated/Login";
import { useMigratedSeo } from "~/composables/useMigratedSeo";

const props = defineProps<{
  content: ContentItem<Login>;
}>();

useMigratedSeo( () => props.content );

const auth = useAuth();
const route = useRoute();
const router = useRouter();

const auditResource = $auditResource();
const memberResource = $isnMemberResource();

const content = ref<any>( null );
const competition = ref<any>( null );

const showAlert = ref( false );

const state = ref<ButtonState>( null );

const contentResource = useContentDeliveryResource();

if ( route.query.cid ) {
  competition.value = await contentResource.getById( route.query.cid );
}

const registerHeading = computed( () => content.value?.registerHeading || "Not got an account?" );
const registerDescription = computed( () => content.value?.registerDescription || null );
const registerReasons = computed( () => content.value?.registerReasons || [] );
const registerUrl = computed( () => {
  let url = "/register?step=join";
  if ( competition.value ) {
    url += `?cid=${competition.value.id}`;
  }
  return url;
} );

watch( state, ( val ) => {
  const watchedStates: ButtonState[] = ["success", "failure"];
  if ( watchedStates.includes( val ) ) {
    if ( val === "failure" ) {
      showAlert.value = true;
    }

    setTimeout( () => {
      state.value = null;
    }, 2000 );
  }
} );

const schema = toTypedSchema( z.object( {
  email: z
    .string()
    .email( "Must be a valid email" )
    .min( 1, "Email is required" ),
  password: z
    .string()
    .min( 10, "Password must be at least 10 characters" )
    .min( 1, "Password is required" ),
} ) );

const { handleSubmit } = useForm( {
  validationSchema: schema,
} );

const submit = handleSubmit( async ( values ) => {
  if ( state.value !== "loading" ) {
    try {
      state.value = "loading";
      await memberResource.login( values );
      await auth.refresh();
      await auditResource.logLogin();

      if ( competition.value ) {
        await navigateTo( competition.value.url );
      } else {
        const { returnUrl } = route.query;
        await navigateTo( returnUrl || "/" );
      }

      state.value = "success";
    } catch ( err ) {
      state.value = "failure";
      console.error( err );
    }
  }
} );

const goBack = ( fallback = "/" ) => {
  if ( !window || window.history.length < 2 ) {
    navigateTo( fallback );
  } else {
    router.back();
  }
};

function onLogin () {
  navigateTo( "/" );
}
</script>

<template>
  <main class="login-page">
    <div class="login-page__content-wrapper">
      <div class="login-page__login">
        <div class="login-page__content">
          <h1 class="h2">
            Welcome back
          </h1>

          <!--          <a-alert -->
          <!--            v-if="showAlert" -->
          <!--            variant="danger" -->
          <!--            role="alert" -->
          <!--            dismissible -->
          <!--            @dismissed="showAlert = false"> -->
          <!--            A user could not be found with that email address and password. -->
          <!--          </a-alert> -->

          <!--          <form @submit.prevent="submit"> -->
          <!--            <AFormField -->
          <!--              v-slot="{ componentField }" -->
          <!--              name="email"> -->
          <!--              <AFormItem> -->
          <!--                <AFormLabel> -->
          <!--                  Email address -->
          <!--                </AFormLabel> -->
          <!--                <AFormControl> -->
          <!--                  <AInput -->
          <!--                    id="email" -->
          <!--                    type="text" -->
          <!--                    v-bind="componentField" -->
          <!--                    placeholder="Enter your email address" -->
          <!--                    autocomplete="email" -->
          <!--                    required /> -->
          <!--                </AFormControl> -->
          <!--                <AFormMessage /> -->
          <!--              </AFormItem> -->
          <!--            </AFormField> -->

          <!--            <AFormField -->
          <!--              v-slot="{ componentField }" -->
          <!--              name="password"> -->
          <!--              <AFormItem> -->
          <!--                <AFormLabel> -->
          <!--                  Password -->
          <!--                </AFormLabel> -->
          <!--                <AFormControl> -->
          <!--                  <APassword -->
          <!--                    id="password" -->
          <!--                    v-bind="componentField" -->
          <!--                    placeholder="Enter your password" -->
          <!--                    autocomplete="current-password" -->
          <!--                    required /> -->
          <!--                </AFormControl> -->
          <!--                <AFormMessage /> -->
          <!--              </AFormItem> -->
          <!--            </AFormField> -->

          <!--            <div class="login-page__actions"> -->
          <!--              <a-button -->
          <!--                v-model="state" -->
          <!--                look="pill" -->
          <!--                class="login-page__submit" -->
          <!--                suffix="fa:light:chevron-right" -->
          <!--                type="submit"> -->
          <!--                Log in -->
          <!--              </a-button> -->
          <!--            </div> -->

          <!--            <NuxtLink -->
          <!--              class="login-page__forgot-password" -->
          <!--              to="/reset-password"> -->
          <!--              Forgot your password? -->
          <!--            </NuxtLink> -->
          <!--          </form> -->

          <LoginForm @logged-in="onLogin" />
        </div>
      </div>

      <div class="login-page__register">
        <div class="login-page__content">
          <h2>{{ registerHeading }}</h2>

          <div
            v-if="registerDescription"
            class="login-page__description"
            v-html="registerDescription" />

          <ul
            v-if="registerReasons.length"
            class="login-page__reasons">
            <li
              v-for="reason in registerReasons"
              :key="reason"
              class="login-page__reason">
              <a-icon icon="fa:solid:check-circle" />
              {{ reason }}
            </li>
          </ul>

          <a-button
            :to="registerUrl"
            suffix="fa:light:chevron-right"
            look="pill"
            class="register-btn">
            Register
          </a-button>

          <a-button
            title="Back to site"
            class="login-page__back"
            @click="goBack">
            <a-icon icon="fa:solid:times" />
          </a-button>
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.login-page {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: stretch;
  align-items: center;
  overflow-y: auto;
  background: var( --bg );
  background: linear-gradient(
          90deg,
          var( --bg ) 0%,
          var( --bg ) 50%,
          var( --primary ) 50%,
          var( --primary ) 100%
  );

  @media ( max-width: 768px ) {
    background: var(--bg) !important;
    align-items: flex-start;
  }

  &__content-wrapper {
    display: flex;
    flex-grow: 1;
    align-items: center;
    background-image: url(/public/login-background.jpg);
    background-position: bottom right;
    background-repeat: no-repeat;
    background-size: 25%;
    height: 100vh;
    > * {
      flex: 0 0 50%;
    }

    @media ( max-width: 768px ) {
      align-items: stretch;
      flex-direction: column;
    }
  }

  &__login,
  &__register {
    flex-direction: column;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__login {
    @media ( max-width: 768px ) {
      margin-top: 30px;
    }
  }

  &__register {
    color: #333;
    @media ( max-width: 768px ) {
      background: var( --primary );
    }

    h2 {
      color: #333;
    }
  }

  &__description {
    margin-bottom: 20px;
  }

  &__reasons {
    margin-bottom: 20px;
    padding: 0;
    list-style: none;
  }

  &__reason {
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    svg {
      display: inline-block;
      margin-right: 10px;
      font-size: 32px;
    }
  }

  &__content {
    width: 100%;
    max-width: 500px;
    padding: 40px;
  }

  &__actions {
    margin-top: var(--s-4);
  }

  &__forgot-password {
    display: block;
    margin-top: 20px;
  }

  &__back {
    position: absolute;
    top: 100px;
    right: 100px;
    background: none;
    border: 0;
    color: #333 !important;
    font-size: 2rem;

    &:hover {
      opacity: 0.7;
    }

    @media ( max-width: 768px ) {
      top: 20px;
      right: 20px;
    }
  }

  .btn svg:not(:first-child):last-child {
    display: inline-block;
    margin-left: 5px;
  }
}

.alert {
  margin-bottom: 15px;
}

.dark-mode .login-page__back {
  color: #fff;
}
.register-btn {
  background-color: #262653;
  color: #fff;
  &:hover {
    border: 1px solid #fff !important;
  }
}

.login-form :deep(.btn) {
  align-self: flex-start;
}
</style>
